import { searchString } from 'lib/stringUtils';

import type { RivalSortedType } from './askKlue.utils.types';
import type { AskKlueQuestionWizardProps } from '../partials/ask-klue/partials/ask-klue-modal-wizard';
import type { SelectItemProps } from '@kluein/klue-ui';
import type { RivalType } from 'api/api.types';

export function normalizeSelectOptions<T extends { id: number; name: string }>(
  dataList: T[],
  searchTerms?: string,
) {
  return dataList.reduce((acc, { id, name }) => {
    if (!searchTerms || searchString(name, searchTerms)) {
      acc.push({
        value: id.toString(),
        label: name,
      });
    }
    return acc;
  }, [] as SelectItemProps[]);
}

export function getFinalPrompt(
  questions: AskKlueQuestionWizardProps[],
  finalPrompt: string,
) {
  return questions.reduce((acc, { answer, promptRef }) => {
    return acc.replace(`{${promptRef}}`, answer);
  }, finalPrompt);
}

export function filterRivalsByCategory(
  rivals: RivalType[],
  companyName?: string,
): RivalSortedType[] {
  return rivals
    .filter((rival) => rival.category === 'company')
    .filter((rival) => rival.battlecardsCount)
    .filter((rival) => rival.cardsCount && rival.cardsCount >= 5)
    .filter((rival) => {
      return !rival.name.toLowerCase().includes('klue');
    })
    .filter((rival) => {
      if (!companyName) return true;
      return !companyName.toLowerCase().includes(rival.name.toLowerCase());
    })
    .sort(
      (rivalA, rivalB) => Number(rivalB.cardsCount) - Number(rivalA.cardsCount),
    )
    .slice(0, 6)
    .map((rival, index) => ({
      ...rival,
      rank: index + 1,
    })) as RivalSortedType[];
}

export function* getRandomPrompt(listLength: number) {
  const promptReturn = Array.from({ length: listLength }, (_, i) => i);
  let i = promptReturn.length;
  while (i--) {
    yield promptReturn.splice(Math.floor(Math.random() * (i + 1)), 1)[0];
  }
}

/**
 * Retrieves a random rival based on the maximum cardsCount cumulative sum.
 *
 * @param rivals The list of Rivals {RivalSortedType[]}
 * @param exclusionList The list of rivals that should not be avoided when picking a random rival
 * @returns A random rival based in it's weighted cumulative sum in the cardsCount array
 */
const getWeightedRandomRivalBasedOnCardsCount = (
  rivals: RivalSortedType[],
  exclusionList: RivalSortedType[] = [],
): RivalSortedType | null => {
  const filteredRivals = rivals.filter(
    (rival) => !exclusionList.includes(rival),
  );

  if (!filteredRivals.length) return null;

  const cardsCount: number[] = [];

  // This is how we pick the weighted cards count.
  // The approach is to basically retrieve every
  // filteredRivals cardsCount and sum it with the
  // cardsCount of the previous rival.
  for (let i = 0; i < filteredRivals.length; i++) {
    cardsCount.push(
      filteredRivals[i].cardsCount + (i > 0 ? cardsCount[i - 1] : 0),
    );
  }

  // Since we have the probability of selection for each rival,
  // we can calculate a random sum value based in this cumulative cards count
  const maxCumulativeCardsCount = cardsCount[cardsCount.length - 1];
  const randomCardsCountSum = maxCumulativeCardsCount * Math.random();

  // Please note that the filteredRivals array and the cardsCount array
  // have both the same length. This allows us to use the cardsCount
  // index as the filteredRivals index without issues
  for (let rivalIndex = 0; rivalIndex < filteredRivals.length; rivalIndex++) {
    if (cardsCount[rivalIndex] >= randomCardsCountSum) {
      return filteredRivals[rivalIndex];
    }
  }

  // This is to avoid complains from TypeScript
  return null;
};

/**
 * Please refer to https://dev.to/trekhleb/weighted-random-algorithm-in-javascript-1pdc
 * so you can understand how this function works. But there's also plenty of comments in it
 * to make it easy for review and maintenance.
 *
 * @param rivals The list of Rivals {RivalSortedType[]}
 * @returns The top three rivals randomly weighted based on their cardsCount
 */
export function getTopTierRivals(rivals: RivalSortedType[]) {
  // In case there is less then 4 rivals we don't need to calculate the top tier
  if (rivals.length < 4) return rivals;

  // Since the top tier list is composed by three rivals,
  // we delegate the finding of these rivals to the function below.
  const topTierRivals: RivalSortedType[] = [];

  for (let i = 0; i < 6; i++) {
    const topTierRival = getWeightedRandomRivalBasedOnCardsCount(
      rivals,
      topTierRivals,
    );
    if (!topTierRival) break;

    topTierRivals.push(topTierRival);
  }

  return topTierRivals;
}
